import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { propTypes } from '../../util/types';

import {
  sendVerificationEmail,
  hasCurrentUserErrors,
} from '../../ducks/user.duck';
import { logout, authenticationInProgress } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

/**
 * Topbar container component, which is connected to Redux Store.
 * @component
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const TopbarContainerComponent = props => {
  const { isAuthenticated, notificationCount = 0, ...rest } = props;

  return <Topbar notificationCount={notificationCount} {...rest} isAuthenticated={isAuthenticated} />;
};

export const resolveTopbarAuthState = state => {
  const auth = state.auth || {};
  const user = state.user || {};
  const currentUser = user.currentUser;
  const hasCurrentUser = !!currentUser?.id;
  const authInfoLoaded = !!auth.authInfoLoaded;
  const authFlag = !!auth.isAuthenticated;
  const isAuthenticated = authInfoLoaded ? authFlag : authFlag || hasCurrentUser;
  return {
    authInfoLoaded,
    authStateReady: authInfoLoaded || hasCurrentUser || authFlag,
    isAuthenticated,
  };
};

const mapStateToProps = state => {
  const { authInfoLoaded, authStateReady, isAuthenticated } = resolveTopbarAuthState(state);
  // Topbar needs isAuthenticated and isLoggedInAs
  const { isLoggedInAs, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserAttentionTransactions,
    currentUserNotificationCount: legacyNotificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const notificationCount = state.notifications?.unreadCount ?? legacyNotificationCount;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));

  return {
    authInfoLoaded,
    authStateReady,
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserAttentionTransactions,
    notificationCount,
    isAuthenticated,
    isLoggedInAs,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(TopbarContainerComponent);

export default TopbarContainer;
